<template>
	<div class="SearchableRadioFilter">
		<Multiselect
			:options="options"
			:model-value="internalValue"
			:mode="MultiselectMode.SINGLE"
			:searchable="true"
			:can-clear="true"
			:max-height="'18rem'"
			:placeholder="placeholder"
			:description="description"
			:warning="warning"
			@updateValues="handleMultiselectUpdate"
		/>

		<div class="List">
			<RadioSelectItem
				v-for="entry in displayedItems"
				:key="entry.item.value"
				:item="entry.item"
				:subItems="entry.subItems"
				:name="groupName"
				@selectionChange="(state, changedItem) => handleItemChange(changedItem || entry.item, state)"
			/>
			<button
				v-if="showMoreIndicator"
				type="button"
				class="ShowAll"
				:aria-expanded="showAll"
				:aria-label="showAll ? 'Show less' : `Show all (${items.length})`"
				@click="showHideAll"
			>
				{{ showAllText }}
			</button>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, watch, computed, type PropType } from 'vue';
import Multiselect from '@components/Multiselect/Multiselect.vue';
import RadioSelectItem from '@components/RadioSelectItem/RadioSelectItem.vue';
import type ISelectItem from '@domain/interfaces/dtos/ISelectItem';
import type { IMultiselectOption } from '@app/interfaces/dtos/IMultiselectOption';
import { SelectListItemStateType } from '@domain/enums/SelectListItemStateType';
import { MultiselectMode } from '@app/enums/MultiselectMode';
import { ItemEntry } from '@app/interfaces/useMultiselect.interface';

const props = defineProps({
	items: {
		type: Array as PropType<ItemEntry[]>,
		required: true
	},
	options: {
		type: Array as PropType<IMultiselectOption[]>,
		required: true
	},
	modelValue: {
		type: [String, Number, null] as PropType<string | number | null>,
		required: true
	},
	placeholder: {
		type: String,
		default: 'Search'
	},
	groupName: {
		type: String,
		default: 'radio-select'
	},
	showLimit: {
		type: Number,
		default: 7
	},
	description: {
		type: String,
		default: ''
	},
	warning: {
		type: String,
		default: ''
	}
});

const showAll = ref(false);

const showMoreIndicator = computed(() => {
	return props.items.length > props.showLimit;
});

const displayedItems = computed(() => {
	if (showAll.value) {
		return props.items;
	}
	return props.items.slice(0, props.showLimit);
});

const showAllText = computed(() => {
	return showAll.value ? `Show less` : `Show all (${props.items.length})`;
});

const showHideAll = () => {
	showAll.value = !showAll.value;
};
const emit = defineEmits<{
	(e: 'update:modelValue', value: string | number | null): void;
	(e: 'select', value: string | number, selected: boolean): void;
}>();

const internalValue = ref<string | number | undefined>(props.modelValue ?? undefined);

watch(() => props.modelValue, (val) => {
	if (val !== internalValue.value) internalValue.value = val ?? undefined;
});

const handleItemChange = (item: ISelectItem, state: string): void => {
	const selected = state === SelectListItemStateType.SELECTED;

	if (selected) {
		internalValue.value = item.value as string | number;
		emit('select', item.value, true);
		emit('update:modelValue', internalValue.value);
	} else if (internalValue.value === item.value) {
		internalValue.value = undefined;
		emit('select', item.value, false);
		emit('update:modelValue', null);
	}
};

const handleMultiselectUpdate = (value: (string | number)[] | string | number | null): void => {
	const normalized = Array.isArray(value) ? (value[0] ?? null) : value ?? null;
	const prev = internalValue.value;

	if (normalized === prev) return;

	if (prev !== undefined) emit('select', prev, false);

	internalValue.value = normalized as string | number | undefined;
	if (normalized !== null) {
		emit('select', normalized as string | number, true);
	}
	emit('update:modelValue', internalValue.value ?? null);
};
</script>

<style scoped lang="scss">
.SearchableRadioFilter {
	display: flex;
	flex-direction: column;
	gap: 1.5rem;
	padding: 0 2px;	// required for outline not to be cut off

	.List {
		display: flex;
		flex-direction: column;
		overflow: auto;
		scrollbar-width: none;
		padding: 2px 2px 0 0;	// required for outline not to be cut off

		&::-webkit-scrollbar {
			display: none;
		}
	}
	.ShowAll {
		display: flex;
		cursor: pointer;
		color: $Blue-700;
		background: none;
		border: none;

		&:focus-visible {
			outline: none;
		}
	}
}
</style>
